/**
 * Kontora BFF – Onboarding-Messages (i18n Foundation)
 *
 * Aktuell nur Deutsch. Struktur ist i18n-ready (kein nested object, sondern
 * flache Keys mit Dot-Notation, damit `react-intl` oder `next-intl` später
 * einfach eingebunden werden kann).
 *
 * Konvention:
 *   - Keys: `bereich.komponente.element`
 *   - Bei Workflows: `workflow.<code>.<feld>` (z. B. `workflow.wf-first-invoice.title`)
 *   - Bei Choice-Buttons: `workflow.<code>.choice.<id>.<label|description>`
 *   - Bei Touren: `tour.<id>.<stepIndex>.<title|content>`
 *
 * Bewusst NICHT zentralisiert:
 *   - Workflow-Texte in `workflows.ts` (title/description/choice.label/choice.description)
 *   - Tour-Texte in `tours.ts` (title/content pro Step)
 *
 *   Diese Inhalte sind strukturierte Datenobjekte, die der Workflow-Runner
 *   bzw. Tour-Overlay direkt konsumiert. Eine Extraktion würde dort viele
 *   Lookups statt simpler Feldzugriffe erzeugen und die Lesbarkeit der
 *   Daten-Definitionen verschlechtern. Falls später eine echte i18n
 *   benötigt wird, können diese Bereiche über einen separaten Datentyp-
 *   Adapter (`t(workflow.code, 'title')`) nachgerüstet werden.
 */

import { messagesEn, type MessageKey as MessageKeyEn } from './messages.en';

export const messages = {
  'welcome.eyebrow': 'Erste Schritte',
  'welcome.greeting.fallback': 'Willkommen bei Kontora!',
  'welcome.greeting.named': 'Hallo {name}!',
  'welcome.description':
    'Kontora ist in sechs Bereiche gegliedert. Wähle unten einen Kontext – wir zeigen dir nur, was du in deiner aktuellen Rolle sehen und nutzen kannst.',
  'welcome.areas.count': 'Verfügbare Bereiche ({count})',
  'welcome.tours.completed': '{count} Tour(en) bereits abgeschlossen.',
  'welcome.button.experienced': 'Ich kenne mich aus – Touren ausblenden',
  'welcome.button.later': 'Ein andermal',
  'welcome.close': 'Schließen',

  'runner.step.counter': 'Schritt {current} / {total}',
  'runner.progress.label': 'Fortschritt im Workflow',
  'runner.summary.heading': 'Bisherige Antworten',
  'runner.button.cancel': 'Abbrechen',
  'runner.button.skip': 'Manuell fortfahren',
  'runner.button.back': '← Zurück',
  'runner.button.confirm': "Los geht's →",
  'runner.confirm.hint': 'Du wirst zur Zielseite weitergeleitet.',

  'empty.eyebrow': 'Noch nichts vorhanden',

  'widget.title': 'Erste Schritte',
  'widget.subtitle': 'Die wichtigsten Aktionen für deinen Start.',

  'banner.power.title': 'Power-Mode aktiv',
  'banner.power.description':
     'Im Power-Mode werden alle Bereiche angezeigt, unabhängig von deiner Rolle.',

  'profile.page.title': 'Mein Profil',
  'profile.page.description':
    'Bearbeite deine persönlichen Daten und Benachrichtigungs-Einstellungen.',
  'profile.displayName': 'Anzeigename',
  'profile.email': 'E-Mail',
  'profile.firstName': 'Vorname',
  'profile.lastName': 'Nachname',
  'profile.phone': 'Telefon',
  'profile.phone.placeholder': '+49 30 1234567',
  'profile.timezone': 'Zeitzone',
  'profile.bio': 'Über mich',
  'profile.bio.hint': 'Wird in Kommentaren und Reviews angezeigt.',
  'profile.bio.placeholder': 'Kurze Beschreibung deiner Rolle im Unternehmen…',
  'profile.notifications': 'Benachrichtigungen',
  'profile.productUpdates': 'Produkt-Updates per E-Mail erhalten',
  'profile.securityAlerts':
    'Sicherheits-Hinweise erhalten (Login von neuem Gerät, Passwort-Änderung)',
  'profile.unassigned': 'Unbenannt',
  'profile.validation.fixErrors': 'Bitte alle Fehler beheben',
  'profile.saved': 'Profil gespeichert',
  'profile.saveError': 'Fehler beim Speichern',
  'profile.save': 'Profil speichern',
  'profile.saving': 'Speichern…',

  'success.title': 'Erfolgreich',
  'loading.default.title': 'Daten werden geladen',
  'loading.default.aria': 'Wird geladen',
  'error.default.title': 'Das hat leider nicht geklappt',
  'error.default.retry': 'Erneut versuchen',
  'error.default.hint':
    'Bitte versuche es nochmal. Bleibt das Problem, wende dich an den Support und gib die Request-ID an.',
  'error.default.requestId': 'Request-ID',

} as const;

export type MessageKey = keyof typeof messages;

type AssertKeysMatch =
  keyof typeof messagesEn extends MessageKeyEn
    ? MessageKeyEn extends keyof typeof messagesEn
      ? keyof typeof messagesEn extends MessageKey
        ? MessageKey extends keyof typeof messagesEn
          ? true
          : never
        : never
      : never
    : never;
const _assertKeysMatch: AssertKeysMatch = true;
void _assertKeysMatch;

export function format(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? `{${key}}`));
}

export const LOCALE = 'de-DE' as const;

export type Locale = 'de-DE' | 'en-US';

const MESSAGES_BY_LOCALE: Record<Locale, Record<MessageKey, string>> = {
  'de-DE': messages,
  'en-US': messagesEn,
};

export function getMessages(locale: Locale = LOCALE): Record<string, string> {
  return MESSAGES_BY_LOCALE[locale] ?? MESSAGES_BY_LOCALE[LOCALE];
}

export function getMessage(key: MessageKey, locale: Locale = LOCALE): string {
  const fromLocale = MESSAGES_BY_LOCALE[locale]?.[key];
  if (fromLocale !== undefined) return fromLocale;
  const fallback = MESSAGES_BY_LOCALE[LOCALE][key as MessageKey];
  return fallback ?? key;
}